import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import { Opening } from 'api-clients/monolith';
import { useOpenings, useSimpleToggle } from 'hooks';
import React, { FC, useCallback, useEffect, useRef, useState } from 'react';

import { FilterDropdownButton } from 'components/FilterDropdown/Button';
import { FilterOpen } from 'components/FilterDropdown/FilterOpen';
import { FilterDropdownProps } from 'components/FilterDropdown/index';
import { Filterable, mapFilterable } from 'components/FilterDropdown/util';
import { useDebounce } from 'hooks/useDebounce';

const PER_PAGE = 50;
const OPENING_MAP_CONFIGS = {
  enterprise: {
    titleAttr: 'funnel_name',
    idAttr: 'funnel_name',
  },
  default: {
    titleAttr: 'funnel_name',
    idAttr: 'funnel_external_id',
  },
};

/**
 * FilterOpeningDropdown provides a dropdown interface for filtering by openings.
 * It fetches openings page by page using the `useOpenings` hook
 * and accumulates the results locally to support infinite scrolling in the `FilterOpen` component.
 */
export const FilterOpeningDropdown: FC<FilterDropdownProps<Filterable>> = ({
  selected,
  setSelected,
  showTitle,
  enterprise,
  userId,
  error: propError,
}) => {
  const { off, showContent: open, toggle: openUp } = useSimpleToggle();
  const [title, setTitle] = useState('');
  const debounceSetQuery = useDebounce(setTitle, 500);
  const { fetchOpenings, openings, isFetching, pagination } = useOpenings();

  const [accumulatedOpenings, setAccumulatedOpenings] = useState<Opening[]>([]);
  const isNewFilterQuery = useRef(true);

  const name = 'opening';

  // Fetch page 1 whenever the search title or other primary filters change.
  useEffect(() => {
    isNewFilterQuery.current = true;
    void fetchOpenings({
      page: 1,
      per_page: PER_PAGE,
      title,
      enterprise,
      user_id: userId,
    });
  }, [fetchOpenings, title, enterprise, userId]);

  // Accumulate openings data fetched by the hook.
  useEffect(() => {
    // Process new openings data only when fetching is complete and data exists.
    if (!isFetching && openings) {
      if (isNewFilterQuery.current || pagination?.currentPage === 1) {
        setAccumulatedOpenings(openings);
      } else {
        setAccumulatedOpenings(prev => {
          const idAttr = enterprise ? 'funnel_name' : 'funnel_external_id';
          const existingIds = new Set(prev.map(op => op[idAttr]));
          const newOpenings = openings.filter(
            op => !existingIds.has(op[idAttr]),
          );
          return [...prev, ...newOpenings];
        });
      }
      isNewFilterQuery.current = false;
    }
    // Intentionally omitting `pagination` from deps. Accumulation logic should only run
    // when `isFetching` becomes false and `openings` potentially contains new data.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openings, isFetching]);

  const fetchNextPage = useCallback(() => {
    if (!isFetching && pagination?.nextPage) {
      void fetchOpenings({
        page: pagination.nextPage,
        per_page: PER_PAGE,
        title,
        enterprise,
        user_id: userId,
      });
    }
  }, [isFetching, pagination, fetchOpenings, title, enterprise, userId]);

  // Prepare the accumulated list of openings for rendering in the dropdown.
  const dropdownOptions = mapFilterable(accumulatedOpenings ?? [], {
    name,
    ...OPENING_MAP_CONFIGS[enterprise ? 'enterprise' : 'default'],
  });

  return (
    <ClickAwayListener onClickAway={off}>
      <div>
        <FilterDropdownButton
          name={name}
          open={open}
          openUp={openUp}
          count={selected.length}
          clearFilter={() => {
            setSelected([]);
          }}
          titles={selected.map(select => select.title)}
          showTitle={showTitle}
          setSelected={setSelected}
          selected={selected}
          error={propError}
        />
        {open && (
          <FilterOpen
            name={name}
            selected={selected}
            setSelected={setSelected}
            options={dropdownOptions}
            setQuery={debounceSetQuery}
            fetchNextPage={fetchNextPage}
          />
        )}
      </div>
    </ClickAwayListener>
  );
};
